import { FC } from "preact/compat";
import { useEffect } from "preact/hooks";
import { useSignalEffect } from "@preact/signals";
import { DynamicSearchTerm, filterProjects } from "~/core";
import ACCESS from "~/core/access";
import { me, useApp } from "~/core/store";
import { useAccessCheck, useSearch } from "~/hooks";
import ContentPasteIcon from "@material-design-icons/svg/outlined/content_paste.svg";
import PauseIcon from "@material-design-icons/svg/outlined/pause.svg";
import PlayIcon from "@material-design-icons/svg/outlined/play_arrow.svg";
import HelpIcon from "@material-design-icons/svg/outlined/question_mark.svg";

const MainSearch: FC = () => {
    const {
        tracker,
        settings,
        search,
        help,
        api: { project, time },
    } = useApp();
    const { isLoading, check } = useAccessCheck();

    const terms = [
        {
            text: "My Profile",
            aliases: ["profile", "account", "my account"],
            link: me("profile"),
            access: ACCESS.profile,
        },
        {
            text: "Settings",
            aliases: ["settings", "my settings", "configuration"],
            link: me("settings"),
            access: ACCESS.settings,
        },
        {
            text: "Chat",
            aliases: ["messages", "messaging", "chat"],
            link: me("chat"),
            access: ACCESS.chat,
        },
        {
            text: "Resources",
            aliases: ["resources", "planning", "resource planning"],
            link: me("resources"),
            access: ACCESS.resources,
        },
        {
            text: "Projects",
            aliases: ["projects", "my projects"],
            link: me("projects"),
            access: ACCESS.projects,
        },
        {
            text: "Time Reporting | Admin",
            aliases: [
                "timelogs",
                "reporting",
                "time reporting",
                "calendar",
                "my logs",
                "logs",
                "timeline",
            ],
            link: me("admin/time"),
            access: ACCESS.adminTime,
        },
        {
            text: "Approval | Admin",
            aliases: ["approvals", "approve"],
            link: me("admin/approval"),
            access: ACCESS.adminApproval,
        },
        {
            text: "Expenses | Admin",
            aliases: ["expenses"],
            link: me("admin/expenses"),
            access: ACCESS.adminExpenses,
        },
        {
            text: "Tasks | Admin",
            aliases: ["admin tasks", "manage tasks", "my tasks"],
            link: me("admin/tasks"),
            access: ACCESS.adminTasks,
        },
        {
            text: "Dashboard",
            aliases: ["home", "main", "dashboard"],
            link: me("dashboard"),
            access: ACCESS.dashboard,
        },
    ];

    useSearch(
        terms.filter((term) => check(term.access)),
        [isLoading],
    );

    useEffect(() => {
        const searchProjects: DynamicSearchTerm = async (query) => {
            if (!query.trim()) {
                return [];
            }

            const show = settings.showInactiveProjects.peek();
            const finalProjects = show
                ? project.allProjects.peek()
                : project.projects.peek();
            const filtered = filterProjects(finalProjects, query);

            return filtered.map((p) => ({
                action: {
                    link: me(`projects/${p.id}/general`),
                },
                appearance: {
                    text: `Project "${p.name}"`,
                    icon: ContentPasteIcon,
                },
            }));
        };

        search.terms.value = [
            ...search.terms.peek(),
            {
                aliases: [
                    "discovery",
                    "discovery mode",
                    "help",
                    "help mode",
                    "guide",
                    "manual",
                    "support",
                ],
                action: {
                    callback: () =>
                        (help.isDiscovery.value = !help.isDiscovery.peek()),
                },
                appearance: {
                    text: "Toggle Discovery (help mode)",
                    icon: HelpIcon,
                },
            },
            searchProjects,
        ];
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        const stopTracker: DynamicSearchTerm = async (query) => {
            const q = query.replace(/\W/g, "").toLowerCase();
            const matches = [
                "stop track",
                "end track",
                "finish track",
                "complete track",
            ].some((i) => i.startsWith(q));

            if (!matches) {
                return [];
            }

            const active =
                tracker.activeTile.peek() ||
                tracker.activeTask.peek() ||
                tracker.activeExternal.peek();
            const activeTrack = active?._track;

            if (!activeTrack) {
                return [];
            }

            return [
                {
                    action: {
                        callback: () => tracker.stop(activeTrack),
                    },
                    appearance: {
                        text: `Stop "${active.name}"`,
                        icon: PauseIcon,
                    },
                },
            ];
        };

        search.terms.value = [...search.terms.peek(), stopTracker];
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useSignalEffect(() => {
        if (time.group.loaded.value) {
            const groups = time.group.trackGroups.peek();
            const filteredGroups = groups.filter(
                (group) =>
                    group.active &&
                    group.project.active &&
                    group.feeCode?.active !== false &&
                    group.feeCategory?.active !== false,
            );

            search.terms.value = search.terms.peek().concat(
                filteredGroups.map((group) => ({
                    aliases: [`start ${group.name}`, group.name],
                    action: {
                        callback: () => tracker.startTile(group._id),
                    },
                    appearance: {
                        text: `Start "${group.name}"`,
                        icon: PlayIcon,
                    },
                })),
            );
        }
    });

    return null;
};

export default MainSearch;
